const MASTERY_MIN_GAME_COUNT = 3;
const MASTERY_MIN_GRADE_SCORE = 0.72;

function parseDateForSort(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return 0;
  const [day, month, year] = dateStr.split("/").map(Number);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return 0;
  return new Date(year, month - 1, day).getTime();
}

function parseTimeForSort(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return 0;
  const [hh, mm] = timeStr.split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh * 60 + mm;
}

function sortGamesNewestFirst(games) {
  return [...games].sort((a, b) => {
    const dateDiff = parseDateForSort(b.date) - parseDateForSort(a.date);
    if (dateDiff !== 0) return dateDiff;
    return parseTimeForSort(b.time) - parseTimeForSort(a.time);
  });
}

function getRetentionGrade(score) {
  if (score >= 0.93) return "A+";
  if (score >= 0.87) return "A";
  if (score >= 0.8) return "A-";
  if (score >= 0.72) return "B+";
  if (score >= 0.63) return "B";
  if (score >= 0.55) return "B-";
  if (score >= 0.47) return "C+";
  if (score >= 0.41) return "C";
  if (score >= 0.35) return "C-";
  if (score >= 0.25) return "D+";
  if (score >= 0.15) return "D";
  return "D-";
}

function computeRetention(stats, lam = 0.8) {
  const last5 = stats?.last5Games || [];

  if (!last5.length || last5.length < 3) {
    return {
      score: null,
      grade: null,
      insufficientData: true,
    };
  }

  const last5Sorted = sortGamesNewestFirst(last5);
  const weights = [];
  const values = [];

  for (let i = 0; i < last5Sorted.length; i += 1) {
    const w = Math.exp(-lam * i);
    let v;

    if (last5Sorted[i].result === "success") {
      v = 1.0;
    } else if (i === 0) {
      v = -0.15;
    } else if (i === 1) {
      v = -0.05;
    } else {
      v = 0.2;
    }

    weights.push(w);
    values.push(v);
  }

  const weightSum = weights.reduce((sum, w) => sum + w, 0);
  let weightedScore =
    weights.reduce((sum, w, idx) => sum + w * values[idx], 0) / weightSum;
  weightedScore = (weightedScore + 0.2) / 1.2;
  weightedScore = Math.max(0, Math.min(1, weightedScore));
  weightedScore = Math.round(weightedScore * 100) / 100;

  return {
    score: weightedScore,
    grade: getRetentionGrade(weightedScore),
    insufficientData: false,
  };
}

function isWordGameMastered(stats) {
  const gameCount = stats.gameCount ?? 0;
  if (gameCount < MASTERY_MIN_GAME_COUNT) return false;

  const retention = stats.retention ?? computeRetention(stats);
  if (retention.insufficientData || retention.score == null) return false;
  return retention.score >= MASTERY_MIN_GRADE_SCORE;
}

function buildMasterySnapshot(last5Games, gameCount) {
  const retention = computeRetention({ last5Games });
  return {
    gameCount,
    isMastered: isWordGameMastered({ gameCount, retention }),
    insufficientData: Boolean(retention.insufficientData),
    retentionScore: retention.score,
  };
}

function computeAnswerMasteryDelta(before, after) {
  if (!before || !after) return 0;

  const beforeMastered = before.isMastered;
  const afterMastered = after.isMastered;

  if (!beforeMastered && afterMastered) return 1;
  if (beforeMastered && !afterMastered) return -1;
  if (beforeMastered && afterMastered) return 0;

  const isThirdGame = before.gameCount === 2 && after.gameCount === 3;
  if (isThirdGame && !after.insufficientData && after.retentionScore != null) {
    return -1;
  }

  return 0;
}

/** Contribution maîtrise (+1 / -1 / 0) du mot lors de la dernière partie. */
export function computeLastGameMasteryDelta(insight) {
  const last5Games = insight?.last5Games;
  const gameCountAtMoment = Number(insight?.gameCountAtMoment);

  if (!Array.isArray(last5Games) || !last5Games.length || !Number.isFinite(gameCountAtMoment)) {
    return 0;
  }

  const sorted = sortGamesNewestFirst(last5Games);
  const beforeGames = sorted.slice(1);
  const afterCount = gameCountAtMoment + 1;

  const before = beforeGames.length
    ? buildMasterySnapshot(beforeGames, gameCountAtMoment)
    : {
        gameCount: gameCountAtMoment,
        isMastered: false,
        insufficientData: true,
        retentionScore: null,
      };

  const after = buildMasterySnapshot(sorted, afterCount);
  return computeAnswerMasteryDelta(before, after);
}
